import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';
import { useAuth } from './AuthContext';
import notificationApi from '../services/notificationApi';

const NotificationContext = createContext();

export const useNotifications = () => {
  const context = useContext(NotificationContext);
  if (!context) {
    throw new Error('useNotifications must be used within a NotificationProvider');
  }
  return context;
};

/**
 * Notification types and priorities
 */
export const NOTIFICATION_TYPES = {
  BATTERY_LOW: 'BATTERY_LOW',
  ROBOT_OFFLINE: 'ROBOT_OFFLINE',
  CRITICAL_STATUS: 'CRITICAL_STATUS',
  JOB_SCHEDULED: 'JOB_SCHEDULED',
  JOB_SUBMITTED: 'JOB_SUBMITTED',
  JOB_PENDING_APPROVAL: 'JOB_PENDING_APPROVAL',
  JOB_APPROVED: 'JOB_APPROVED',
  JOB_REJECTED: 'JOB_REJECTED',
  JOB_COMPLETED: 'JOB_COMPLETED',
  WEATHER_SNOW: 'WEATHER_SNOW',
  WEATHER_RAIN: 'WEATHER_RAIN',
  WEATHER_HEAT: 'WEATHER_HEAT',
  WEATHER_COLD: 'WEATHER_COLD',
  GENERAL: 'GENERAL'
};

export const NOTIFICATION_PRIORITY = {
  HIGH: 'HIGH',     // Critical issues (battery, critical status)
  MEDIUM: 'MEDIUM', // Important events (job approved, completed)
  LOW: 'LOW'        // Informational (job scheduled)
};

/**
 * Notification Provider Component
 * 
 * Manages global notification state with features:
 * - Deduplication (prevents duplicate notifications for same entity)
 * - Priority-based display order
 * - Auto-dismiss with configurable duration
 * - Action buttons support
 */
export const NotificationProvider = ({ children }) => {
  const { user, isAuthenticated } = useAuth();
  const [notifications, setNotifications] = useState([]);
  const [seenNotificationIds, setSeenNotificationIds] = useState(new Set());
  const [isLoading, setIsLoading] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);

  /**
   * Fetch notifications from database
   */
  const fetchNotifications = useCallback(async () => {
    if (!isAuthenticated || !user || user.notificationsEnabled === false) {
      if (user?.notificationsEnabled === false) {
        setNotifications([]);
        setUnreadCount(0);
      }
      return;
    }

    try {
      setIsLoading(true);
      const response = await notificationApi.getNotifications({ 
        limit: 100,
        unreadOnly: false 
      });
      
      if (response.success && Array.isArray(response.data)) {
        // Convert database notifications to context format
        const dbNotifications = response.data.map(notif => ({
          id: notif.id,
          title: notif.title,
          message: notif.message,
          type: notif.type,
          priority: notif.priority,
          timestamp: new Date(notif.createdAt),
          metadata: notif.metadata || {},
          isRead: notif.isRead,
          readAt: notif.readAt ? new Date(notif.readAt) : null,
          duration: 0, // Database notifications don't auto-dismiss
          actions: []
        }));

        // Merge with existing in-memory notifications (avoid duplicates)
        setNotifications(prev => {
          const existingIds = new Set(prev.map(n => n.id));
          const newDbNotifications = dbNotifications.filter(n => !existingIds.has(n.id));
          const allNotifications = [...prev, ...newDbNotifications];
          
          // Sort by priority and timestamp
          const priorityOrder = { HIGH: 3, MEDIUM: 2, LOW: 1 };
          return allNotifications.sort((a, b) => {
            const priorityDiff = priorityOrder[b.priority] - priorityOrder[a.priority];
            if (priorityDiff !== 0) return priorityDiff;
            return new Date(b.timestamp) - new Date(a.timestamp);
          });
        });

        setUnreadCount(response.unreadCount || 0);
      }
    } catch (error) {
      console.error('Error fetching notifications from database:', error);
    } finally {
      setIsLoading(false);
    }
  }, [isAuthenticated, user]);

  // Fetch notifications on mount and when user changes
  useEffect(() => {
    if (isAuthenticated && user) {
      fetchNotifications();
      
      // Poll for new notifications every 30 seconds
      const interval = setInterval(fetchNotifications, 30000);
      return () => clearInterval(interval);
    }
  }, [isAuthenticated, user, fetchNotifications]);

  /**
   * Generate unique notification ID
   * @param {string} type - Notification type
   * @param {string} robotId - Robot ID (if applicable)
   * @param {string} entityId - Additional entity identifier (job ID, etc.)
   * @returns {string} Unique notification ID
   */
  const generateNotificationId = (type, robotId, entityId) => {
    return `${type}_${robotId || 'global'}_${entityId || 'default'}`;
  };

  /**
   * Add a new notification
   * 
   * @param {Object} notification
   * @param {string} notification.title - Notification title
   * @param {string} notification.message - Notification message
   * @param {string} notification.type - Type from NOTIFICATION_TYPES
   * @param {string} notification.priority - Priority from NOTIFICATION_PRIORITY
   * @param {number} notification.duration - Auto-dismiss duration in ms (default: 5000, 0 = persist)
   * @param {Object} notification.metadata - Additional data (robotId, robotName, etc.)
   * @param {Array} notification.actions - Action buttons (optional)
   * @param {boolean} notification.deduplicate - Whether to prevent duplicates (default: true)
   * @param {string} notification.userId - Target user ID (optional, defaults to current user)
   * @param {boolean} notification.saveToDb - Whether to save to database (default: true)
   * @returns {string} Notification ID
   */
  const addNotification = useCallback(async (notification) => {
    if (user?.notificationsEnabled === false) {
      return notification.id || null;
    }

    const {
      title,
      message,
      type = NOTIFICATION_TYPES.GENERAL,
      priority = NOTIFICATION_PRIORITY.MEDIUM,
      duration = 5000,
      metadata = {},
      actions = [],
      deduplicate = true,
      userId = user?.id,
      saveToDb = true
    } = notification;

    // Generate notification ID for deduplication
    const notificationId = notification.id || generateNotificationId(
      type,
      metadata.robotId,
      metadata.entityId || metadata.jobId
    );

    // Check for duplicate if deduplication is enabled
    if (deduplicate && seenNotificationIds.has(notificationId)) {
      console.log(`[NotificationContext] Skipping duplicate notification: ${notificationId}`);
      return notificationId;
    }

    const newNotification = {
      id: notificationId,
      title,
      message,
      type,
      priority,
      timestamp: new Date(),
      metadata,
      actions,
      duration,
      isRead: false,
      readAt: null
    };

    // Save to database if user is authenticated and saveToDb is true
    if (isAuthenticated && user && saveToDb && userId) {
      try {
        await notificationApi.createNotification({
          userId,
          type,
          title,
          message,
          priority,
          metadata
        });
        console.log(`[NotificationContext] Saved notification to database: ${notificationId}`);
      } catch (error) {
        console.error(`[NotificationContext] Failed to save notification to database:`, error);
        // Continue anyway - notification will still show in UI
      }
    }

    // Add to seen set
    setSeenNotificationIds(prev => new Set(prev).add(notificationId));

    // Add to notifications array
    setNotifications(prev => {
      // Check if notification already exists (from database)
      const exists = prev.find(n => n.id === notificationId);
      if (exists) {
        // Update existing notification
        return prev.map(n => n.id === notificationId ? newNotification : n);
      }
      
      // Sort by priority: HIGH > MEDIUM > LOW
      const priorityOrder = { HIGH: 3, MEDIUM: 2, LOW: 1 };
      const sorted = [...prev, newNotification].sort((a, b) => {
        const priorityDiff = priorityOrder[b.priority] - priorityOrder[a.priority];
        if (priorityDiff !== 0) return priorityDiff;
        return new Date(b.timestamp) - new Date(a.timestamp);
      });
      return sorted;
    });

    // Update unread count
    if (!newNotification.isRead) {
      setUnreadCount(prev => prev + 1);
    }

    console.log(`[NotificationContext] Added notification: ${notificationId}`, newNotification);

    // Auto-dismiss if duration is set (only for in-memory notifications)
    if (duration > 0 && !saveToDb) {
      setTimeout(() => {
        removeNotification(notificationId);
      }, duration);
    }

    return notificationId;
  }, [seenNotificationIds, isAuthenticated, user]);

  /**
   * Remove a notification by ID (marks as read, doesn't delete from DB)
   * @param {string} notificationId 
   */
  const removeNotification = useCallback(async (notificationId) => {
    // Find the notification we're removing
    const target = notifications.find(n => n.id === notificationId);
    const isBatteryLow = target?.type === NOTIFICATION_TYPES.BATTERY_LOW;

    // For BATTERY_LOW, treat closing one as closing all battery warnings
    const idsToAffect = isBatteryLow
      ? notifications.filter(n => n.type === NOTIFICATION_TYPES.BATTERY_LOW).map(n => n.id)
      : [notificationId];

    // Mark as read in database for affected notifications (best-effort)
    if (isAuthenticated && user) {
      for (const id of idsToAffect) {
        try {
          await notificationApi.markAsRead(id);
        } catch (apiError) {
          console.warn('Error marking notification as read in database (may be in-memory only):', apiError);
        }
      }
    }

    // Remove affected notifications from local state
    setNotifications(prev => {
      const updated = prev.filter(n => !idsToAffect.includes(n.id));
      const newUnreadCount = updated.filter(n => !n.isRead).length;
      setUnreadCount(newUnreadCount);
      return updated;
    });
  }, [isAuthenticated, user, notifications]);

  /**
   * Clear all notifications (marks all as read in DB)
   */
  const clearAll = useCallback(async () => {
    if (isAuthenticated && user) {
      try {
        await notificationApi.markAllAsRead();
        // Refresh notifications to get updated read status
        await fetchNotifications();
      } catch (error) {
        console.error('Error marking all notifications as read:', error);
      }
    }
    
    // Clear from local state (but keep DB notifications marked as read)
    setNotifications(prev => prev.map(n => ({ ...n, isRead: true })));
    setUnreadCount(0);
  }, [isAuthenticated, user, fetchNotifications]);

  /**
   * Clear seen notification IDs (for testing or reset)
   */
  const clearSeenIds = useCallback(() => {
    setSeenNotificationIds(new Set());
  }, []);

  /**
   * Mark notification as read
   */
  const markAsRead = useCallback(async (notificationId) => {
    if (isAuthenticated && user) {
      try {
        await notificationApi.markAsRead(notificationId);
        // Update local state
        setNotifications(prev => {
          const updated = prev.map(n => 
            n.id === notificationId ? { ...n, isRead: true, readAt: new Date() } : n
          );
          const newUnreadCount = updated.filter(n => !n.isRead).length;
          setUnreadCount(newUnreadCount);
          return updated;
        });
      } catch (error) {
        console.error('Error marking notification as read:', error);
      }
    }
  }, [isAuthenticated, user]);

  /**
   * Delete notification (permanent deletion)
   */
  const deleteNotification = useCallback(async (notificationId) => {
    if (isAuthenticated && user) {
      try {
        // Try to delete from database if it's a DB notification
        // Check if it's a database notification (has DB ID format - CUIDs are typically 25 chars, UUIDs are 36)
        // Also check if notification has a database ID property
        const notification = notifications.find(n => n.id === notificationId);
        const isDbNotification = notification && (
          notificationId.length > 20 || // CUID or UUID format
          notification.dbId || // Has database ID property
          notification.createdAt // Has createdAt from database
        );

        if (isDbNotification) {
          try {
            await notificationApi.deleteNotification(notificationId);
          } catch (apiError) {
            // If API delete fails (e.g., notification doesn't exist in DB), 
            // still remove from local state
            console.warn('Error deleting notification from database (may be in-memory only):', apiError);
          }
        }

        // Always remove from local state, regardless of DB deletion result
        setNotifications(prev => {
          const updated = prev.filter(n => n.id !== notificationId);
          const newUnreadCount = updated.filter(n => !n.isRead).length;
          setUnreadCount(newUnreadCount);
          return updated;
        });
      } catch (error) {
        console.error('Error deleting notification:', error);
        // Still remove from local state even if API call fails
        setNotifications(prev => {
          const updated = prev.filter(n => n.id !== notificationId);
          const newUnreadCount = updated.filter(n => !n.isRead).length;
          setUnreadCount(newUnreadCount);
          return updated;
        });
      }
    } else {
      // If not authenticated, still remove from local state
      setNotifications(prev => {
        const updated = prev.filter(n => n.id !== notificationId);
        const newUnreadCount = updated.filter(n => !n.isRead).length;
        setUnreadCount(newUnreadCount);
        return updated;
      });
    }
  }, [isAuthenticated, user, notifications]);

  const value = {
    notifications,
    addNotification,
    removeNotification,
    clearAll,
    clearSeenIds,
    unreadCount,
    isLoading,
    markAsRead,
    deleteNotification,
    fetchNotifications,
    refreshNotifications: fetchNotifications
  };

  return (
    <NotificationContext.Provider value={value}>
      {children}
    </NotificationContext.Provider>
  );
};

export default NotificationContext;

